/**
 * Import de la liste officielle des résultats (lots 2 et 3, 01/11/2026).
 *
 * Une page de la liste de l'examen, pas une fenêtre : le rapprochement montre
 * des dizaines de lignes, et la navigation de KLASYS ouvre ce genre de détail
 * sur place (drill-in). Trois étapes :
 *
 *   1. Fichier     Excel, CSV ou PDF, lu dans le navigateur ; la nature de la
 *                  liste (les admis seulement, ou tous les candidats).
 *   2. Colonnes    quelle colonne porte le nom, le matricule national, la date
 *                  de naissance, le résultat, la mention - reconnues d'après
 *                  les titres, corrigeables.
 *   3. Rapprochement  les trois paniers rendus par le serveur, que la
 *                  personne valide avant que rien ne soit écrit.
 */
import React, { useMemo, useState } from 'react';
import { Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import ChampSelect from '../ui/ChampSelect';
import FilArianeExamen from './FilArianeExamen';
import RapprochementResultats from './RapprochementResultats';
import PiedActions from './PiedActions';
import ControleLecture from './ControleLecture';
import ZoneFichier from './ZoneFichier';
import { api } from '../../services/api';
import {
  CHAMPS,
  lireFichier,
  detecterEntetes,
  construireLignes,
  classerLignes,
  controleLecture,
  lettreColonne
} from './lectureListeOfficielle';

const ETAPES = ['Fichier', 'Colonnes', 'Rapprochement'];

const AUCUNE = 'aucune';

const TYPES_LISTE = [
  {
    valeur: 'admis',
    libelle: 'Les admis seulement',
    aide: "Chaque ligne est un admis. Ceux de nos candidats qui n'y figurent pas pourront être déclarés ajournés."
  },
  {
    valeur: 'complete',
    libelle: 'Tous les candidats, avec leur résultat',
    aide: 'La liste dit pour chacun admis, ajourné ou absent : une colonne Résultat est nécessaire.'
  }
];

const Etapes = ({ courante }) => (
  <div className="mb-6 flex flex-wrap items-center gap-2">
    {ETAPES.map((libelle, i) => {
      const numero = i + 1;
      const etat = numero === courante ? 'courante' : numero < courante ? 'faite' : 'a_venir';
      return (
        <div
          key={libelle}
          className={`inline-flex h-8 items-center gap-2 rounded-lg border px-3 text-xs font-semibold ${etat === 'courante'
            ? 'border-[#246990] bg-[#246990] text-white'
            : etat === 'faite'
              ? 'border-[#246990]/30 bg-[#eef7fb] text-[#246990]'
              : 'border-gray-200 bg-white text-gray-400'
            }`}
        >
          <span>{numero}</span>
          {libelle}
        </div>
      );
    })}
  </div>
);

const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';
const BOUTON_PLEIN = `${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50`;

const ImportResultats = ({ examen, onRetour, onTermine, notifier }) => {
  const { code, libelleLong } = examen;

  const [etape, setEtape] = useState(1);
  const [lecture, setLecture] = useState(false);
  const [fichier, setFichier] = useState(null); // { nom, format, feuilles }
  const [indexFeuille, setIndexFeuille] = useState(0);
  const [typeListe, setTypeListe] = useState('admis');
  const [indexEntete, setIndexEntete] = useState(-1);
  const [correspondance, setCorrespondance] = useState({});
  const [analyse, setAnalyse] = useState(null);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);

  const feuille = fichier?.feuilles[indexFeuille] || null;
  const enBloc = Boolean(feuille?.enBloc);

  // Les titres reconnus, appliqués à la feuille choisie. La nature de la liste
  // suit : une colonne Résultat laisse penser à une liste complète.
  const appliquerDetection = (laFeuille) => {
    if (!laFeuille || laFeuille.enBloc) {
      setIndexEntete(-1);
      setCorrespondance({});
      return;
    }
    const detection = detecterEntetes(laFeuille.lignes);
    setIndexEntete(detection.indexEntete);
    setCorrespondance(detection.correspondance);
    setTypeListe(detection.correspondance.resultat !== undefined ? 'complete' : 'admis');
  };

  const choisirFichier = async (leFichier) => {
    if (!leFichier) return;
    setLecture(true);
    setAnalyse(null);
    try {
      const lu = await lireFichier(leFichier);
      const avecLignes = lu.feuilles.findIndex((f) => f.lignes.length > 0);
      if (avecLignes < 0) throw new Error('Le fichier ne contient aucune ligne lisible.');
      setFichier({ nom: leFichier.name, format: lu.format, feuilles: lu.feuilles });
      setIndexFeuille(avecLignes);
      appliquerDetection(lu.feuilles[avecLignes]);
    } catch (erreur) {
      setFichier(null);
      notifier(erreur.message || 'Le fichier ne peut pas être lu.', 'error');
    } finally {
      setLecture(false);
    }
  };

  const changerFeuille = (valeur) => {
    const index = Number(valeur);
    setIndexFeuille(index);
    appliquerDetection(fichier.feuilles[index]);
  };

  const nombreColonnes = useMemo(
    () => (feuille ? Math.max(0, ...feuille.lignes.slice(0, 200).map((l) => l.length)) : 0),
    [feuille]
  );

  const titres = useMemo(
    () => (indexEntete >= 0 && feuille ? feuille.lignes[indexEntete] : []),
    [indexEntete, feuille]
  );
  const optionsColonnes = useMemo(() => [
    { valeur: AUCUNE, libelle: 'Aucune' },
    ...Array.from({ length: nombreColonnes }, (_, i) => ({
      valeur: String(i),
      libelle: `${lettreColonne(i)}${titres[i] ? ` - ${String(titres[i]).slice(0, 40)}` : ''}`
    }))
  ], [nombreColonnes, titres]);

  const optionsEntete = useMemo(() => [
    { valeur: '-1', libelle: 'Aucune : la feuille commence par les données' },
    ...(feuille?.lignes || []).slice(0, 30).map((l, i) => ({
      valeur: String(i),
      libelle: `Ligne ${i + 1} : ${l.filter(Boolean).join(' | ').slice(0, 70) || '(vide)'}`
    }))
  ], [feuille]);

  const lignesConstruites = useMemo(() => (feuille
    ? construireLignes(feuille.lignes, enBloc ? -1 : indexEntete, correspondance, { enBloc })
    : []), [feuille, indexEntete, correspondance, enBloc]);

  // Seules les lignes d'élèves partent au rapprochement ; le texte du document
  // (en-têtes, titres, pied de page) est montré à part, replié.
  const classement = useMemo(
    () => classerLignes(lignesConstruites, { enBloc, correspondance }),
    [lignesConstruites, enBloc, correspondance]
  );
  const lignesAEnvoyer = classement.eleves;
  const controle = useMemo(
    () => controleLecture({ eleves: classement.eleves, texteBrut: feuille?.texteBrut || [] }),
    [classement, feuille]
  );

  const aUnNom = enBloc || ['nom_complet', 'nom', 'prenom', 'matricule_national']
    .some((c) => correspondance[c] !== undefined);
  const sansColonneDeNom = !enBloc && aUnNom
    && !['nom_complet', 'nom', 'prenom'].some((c) => correspondance[c] !== undefined);
  const resultatRequis = typeListe === 'complete' && !enBloc && correspondance.resultat === undefined;
  const colonnesPretes = aUnNom && !resultatRequis && lignesAEnvoyer.length > 0;

  const apercu = lignesAEnvoyer.slice(0, 8);
  const champsUtilises = CHAMPS.filter((c) => correspondance[c.cle] !== undefined);

  const rapprocher = async () => {
    setAnalyseEnCours(true);
    try {
      const reponse = await api.examens.analyserImportResultats({
        examen_type: code,
        type_liste: typeListe,
        lignes: lignesAEnvoyer
      });
      if (!reponse?.success) throw new Error(reponse?.message || reponse?.error || 'Rapprochement impossible');
      setAnalyse(reponse.data);
      setEtape(3);
    } catch (erreur) {
      notifier(erreur.message || 'Rapprochement impossible', 'error');
    } finally {
      setAnalyseEnCours(false);
    }
  };

  const confirmer = async (charge) => {
    const reponse = await api.examens.confirmerImportResultats({
      examen_type: code,
      type_liste: typeListe,
      nom_fichier: fichier?.nom || null,
      format: fichier?.format || null,
      lignes_fichier: lignesConstruites.length,
      ...charge
    });
    if (!reponse?.success) throw new Error(reponse?.message || reponse?.error || "L'enregistrement a échoué");
    notifier(reponse.message || 'Résultats enregistrés');
    onTermine();
  };

  return (
    <div>
      <FilArianeExamen
        codeCourant={code}
        libelleCourant={libelleLong}
        onRetour={onRetour}
        onRetourNiveau={onRetour}
        enfant="Import de la liste officielle"
      />

      <div className="mb-4">
        <h2 className="text-xl font-semibold text-[#246990]">Importer la liste officielle - {libelleLong}</h2>
        <p className="text-sm text-gray-500">
          Le fichier est lu sur cet ordinateur : il n&apos;est envoyé nulle part, seules ses lignes servent au rapprochement.
        </p>
      </div>

      <Etapes courante={etape} />

      {etape === 1 && (
        <>
          <Card>
            <CardContent className="space-y-5 p-5">
              <div>
                <p className="mb-2 text-sm font-semibold text-gray-900">La liste officielle</p>
                <ZoneFichier
                  fichier={fichier}
                  lignesLues={feuille?.lignes.length || 0}
                  lecture={lecture}
                  onChoisir={choisirFichier}
                />
              </div>

              {fichier && fichier.feuilles.length > 1 && (
                <div className="max-w-md space-y-2">
                  <p className="text-sm text-gray-700">Feuille du classeur</p>
                  <ChampSelect
                    valeur={String(indexFeuille)}
                    onChange={changerFeuille}
                    options={fichier.feuilles.map((f, i) => ({
                      valeur: String(i),
                      libelle: `${f.nom} (${f.lignes.length} lignes)`
                    }))}
                    className="w-full"
                  />
                </div>
              )}

              {fichier && (
                <div>
                  <p className="mb-2 text-sm font-semibold text-gray-900">Ce que contient la liste</p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {TYPES_LISTE.map((type) => (
                      <button
                        key={type.valeur}
                        type="button"
                        onClick={() => setTypeListe(type.valeur)}
                        className={`rounded-lg border p-4 text-left transition-colors ${typeListe === type.valeur
                          ? 'border-[#246990] bg-[#eef7fb]'
                          : 'border-gray-200 bg-white hover:border-[#246990]/40'
                          }`}
                      >
                        <p className={`text-sm font-semibold ${typeListe === type.valeur ? 'text-[#246990]' : 'text-gray-900'}`}>
                          {type.libelle}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">{type.aide}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {enBloc && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Aucune ligne de titres n&apos;a été reconnue dans ce PDF : chaque ligne sera comparée en entier
                  aux noms de nos candidats. Le rapprochement marche, mais vérifiez bien les lignes à confirmer.
                </div>
              )}
            </CardContent>
          </Card>

          <PiedActions>
            <Button variant="outline" onClick={onRetour} className={BOUTON_PIED}>
              Annuler
            </Button>
            <Button
              onClick={() => (enBloc ? rapprocher() : setEtape(2))}
              disabled={!fichier || lecture || analyseEnCours || (enBloc && lignesAEnvoyer.length === 0)}
              className={BOUTON_PLEIN}
            >
              {analyseEnCours ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {enBloc ? 'Rapprocher' : 'Continuer'}
            </Button>
          </PiedActions>
        </>
      )}

      {etape === 2 && feuille && (
        <>
          <Card>
            <CardContent className="space-y-5 p-5">
              <div className="max-w-2xl space-y-2">
                <p className="text-sm font-semibold text-gray-900">Ligne des titres</p>
                <ChampSelect
                  valeur={String(indexEntete)}
                  onChange={(valeur) => {
                    const index = Number(valeur);
                    setIndexEntete(index);
                    if (index >= 0) setCorrespondance(detecterEntetes([feuille.lignes[index]]).correspondance);
                  }}
                  options={optionsEntete}
                  className="w-full"
                />
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-gray-900">Colonne de chaque information</p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                  {CHAMPS.map((champ) => (
                    <div key={champ.cle} className="space-y-1">
                      <p className="text-xs text-gray-500">{champ.libelle}</p>
                      <ChampSelect
                        valeur={correspondance[champ.cle] !== undefined ? String(correspondance[champ.cle]) : AUCUNE}
                        onChange={(valeur) => setCorrespondance((precedente) => {
                          const suivante = { ...precedente };
                          if (valeur === AUCUNE) delete suivante[champ.cle];
                          else suivante[champ.cle] = Number(valeur);
                          return suivante;
                        })}
                        options={optionsColonnes}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
                {!aUnNom && (
                  <p className="mt-2 text-xs text-red-600">
                    Indiquez au moins la colonne du nom, ou celle du matricule national.
                  </p>
                )}
                {sansColonneDeNom && (
                  <p className="mt-2 text-xs text-amber-700">
                    Sans colonne de nom, seuls les matricules nationaux déjà connus de KLASYS seront reconnus :
                    au premier import d&apos;une liste, presque aucun. Indiquez la colonne du nom si la liste en a une.
                  </p>
                )}
                {resultatRequis && (
                  <p className="mt-2 text-xs text-red-600">
                    Une liste de tous les candidats a besoin de sa colonne Résultat.
                    Sinon, revenez à l&apos;étape 1 et choisissez « Les admis seulement ».
                  </p>
                )}
              </div>

              {champsUtilises.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-semibold text-gray-900">
                    Aperçu <span className="font-normal text-gray-500">
                      - {lignesAEnvoyer.length} ligne{lignesAEnvoyer.length > 1 ? 's' : ''} d&apos;élèves
                      {classement.texte.length > 0
                        && `, ${classement.texte.length} ligne${classement.texte.length > 1 ? 's' : ''} de texte du document mise${classement.texte.length > 1 ? 's' : ''} de côté`}
                    </span>
                  </p>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">Ligne</th>
                          {champsUtilises.map((c) => (
                            <th key={c.cle} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                              {c.libelle}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {apercu.map((ligne) => (
                          <tr key={ligne.ligne}>
                            <td className="px-3 py-2 text-xs text-gray-400 tabular-nums">{ligne.ligne}</td>
                            {champsUtilises.map((c) => (
                              <td key={c.cle} className="px-3 py-2 text-sm text-gray-800">{ligne[c.cle] || ''}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {aUnNom && lignesAEnvoyer.length > 0 && <ControleLecture controle={controle} />}
            </CardContent>
          </Card>

          <PiedActions>
            <Button variant="outline" onClick={() => setEtape(1)} className={BOUTON_PIED}>
              <ArrowLeft size={16} />
              Retour
            </Button>
            <Button onClick={rapprocher} disabled={!colonnesPretes || analyseEnCours} className={BOUTON_PLEIN}>
              {analyseEnCours ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              Rapprocher
            </Button>
          </PiedActions>
        </>
      )}

      {etape === 3 && analyse && (
        <RapprochementResultats
          key={`${fichier?.nom}-${analyse.lignes_lues}`}
          analyse={analyse}
          texteIgnore={classement.texte}
          controle={controle}
          typeListe={typeListe}
          libelleLong={libelleLong}
          onRetour={() => setEtape(enBloc ? 1 : 2)}
          onConfirmer={confirmer}
          notifier={notifier}
        />
      )}
    </div>
  );
};

export default ImportResultats;
